import { useState } from "react";
import { Link, Outlet, useNavigate, useRouteLoaderData, useSubmit } from "react-router-dom";
import UserInfo from "src/components/common/userInfo";
import {
    getDateEndFromWeekNum,
    getDatefromWeekNum,
    locatorWeeks,
    myAxios,
    statusColorCode,
    weekDays,
} from "src/components/common/functions";
import { LocatorWeekData } from "./actions_loaders";

type LoaderData = {
    week: string;
    locator: {
        id: string;
        email: string;
        userName: string;
        lanId?: string;
        outside: string;
        hireDate: string;
        quitDate?: string;
    };
    weekData: LocatorWeekData;
};

export default function AllLocatorDetail() {
    const { week, locator, weekData } = useRouteLoaderData("AllLocatorDetail") as LoaderData;
    const navigate = useNavigate();
    const submit = useSubmit();
    const [switchWeek, setSwitchWeek] = useState("");

    const handleWithdraw = () => {
        if (!window.confirm(`Are you sure to withdraw ${locator.userName}'s time cards for the week?`)) return;
        submit(new FormData(), { method: "post", action: "Withdraw" });
    };

    const handleAddEmergency = () => {
        submit(new FormData(), { method: "post", action: "AddEmergency" });
    };

    const handleDeleteEmergency = (index: number) => {
        if (!window.confirm("Are you sure to delete this emergency call out?")) return;
        const formData = new FormData();
        formData.append("index", String(index));
        submit(formData, { method: "post", action: "DeleteEmergency" });
    };

    const handleSwitchDay = () => {
        const fromDay = window.prompt("From Date:\n(1 for Monday, 2 for Tuesday... 7 for Sunday)");
        if (!fromDay) return;
        const toDay = window.prompt("To Date:\n(1 for Monday, 2 for Tuesday... 7 for Sunday)");
        if (!toDay) return;
        const fromIndex = Number(fromDay) - 1;
        const toIndex = Number(toDay) - 1;
        if ([fromIndex, toIndex].some(v => Number.isNaN(v) || v < 0 || v > 6)) {
            window.alert("Input not valid.");
            return;
        }
        const formData = new FormData();
        formData.append("fromIndex", String(fromIndex));
        formData.append("toIndex", String(toIndex));
        submit(formData, { method: "post", action: "SwitchDay" });
    };

    const handleSwitchWeek = () => {
        if (!switchWeek) {
            window.alert("Select a target week first.");
            return;
        }
        const formData = new FormData();
        formData.append("toWk", switchWeek);
        submit(formData, { method: "post", action: "SwitchWeek" });
    };

    const openPdf = async (id: string, fileName: string) => {
        const formData = new FormData();
        formData.append("id", id);
        const res = await myAxios({ fileName: "outside/locator/getPDF.php", formData });
        if (!res?.data) return;
        const anchor = document.createElement("a");
        anchor.href = String(res.data);
        anchor.download = fileName;
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
        anchor.click();
    };

    return (
        <div className = "pb8rem px-3">
            <nav className = "projectHeader d-flex align-items-center justify-content-between flex-wrap gap-3">
                <span>L&M Outside Status</span>
                <button type = "button" className = "btn btn-outline-primary" onClick = {() => navigate(`/OutsideStatus/${week}`)}>Back</button>
            </nav>

            <div className = "my-3 border rounded bg-white p-3">
                <div className = "d-flex flex-wrap gap-4">
                    <div>
                        <div className = "small text-muted fw-bold">Employee</div>
                        <UserInfo name = {locator.userName.replace(/ {2}/g, " ")} id = {locator.id || locator.email} />
                    </div>
                    <div>
                        <div className = "small text-muted fw-bold">Week</div>
                        <div>{getDateEndFromWeekNum(week)}</div>
                    </div>
                    <div>
                        <div className = "small text-muted fw-bold">Status</div>
                        <div>{weekData.status || "Created"}</div>
                    </div>
                    <div>
                        <div className = "small text-muted fw-bold">Outside</div>
                        <div>{locator.outside}</div>
                    </div>
                </div>
            </div>

            <div className = "mb-3 d-flex flex-wrap gap-2">
                <button type = "button" className = "btn btn-outline-primary" onClick = {handleWithdraw}>Withdraw Week</button>
                <button type = "button" className = "btn btn-outline-primary" onClick = {handleAddEmergency}>Add EOT</button>
                <button type = "button" className = "btn btn-outline-primary" onClick = {handleSwitchDay}>Switch Day</button>
                <select className = "form-control inline" style = {{ maxWidth: 180 }} value = {switchWeek} onChange = {e => setSwitchWeek(e.currentTarget.value)}>
                    <option value = "">Switch To Week</option>
                    {locatorWeeks.filter(value => value !== week).map((value, index) => (
                        <option key = {index} value = {value}>{getDateEndFromWeekNum(value)}</option>
                    ))}
                </select>
                <button type = "button" className = "btn btn-outline-primary" onClick = {handleSwitchWeek}>Switch Week</button>
            </div>

            <div className = "table-responsive mb-4">
                <table className = "table">
                    <thead className = "noTableTopBorder">
                        <tr>
                            <th>Day</th>
                            <th>Date</th>
                            <th>Status</th>
                            <th>Work Status</th>
                            <th>Tickets</th>
                            <th />
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {(weekData.form ?? []).map((day: any, index: number) => (
                            <tr key = {index}>
                                <td>{weekDays[index]}</td>
                                <td>{getDatefromWeekNum(week, index)}</td>
                                <td style = {{ color: statusColorCode(day.status || "Created", "Work") }}>{day.status || "Created"}</td>
                                <td>{day.workStatus || "Work"}</td>
                                <td>{day.data?.filter((item: any) => item.category === "job").length ?? 0}</td>
                                <td>
                                    <Link className = "btn btn-outline-primary" to = {`day/${index}/Edit`}>Edit</Link>
                                </td>
                                <td>
                                    {day.id && String(day.id).length === 32 ? (
                                        <button
                                            type = "button"
                                            className = "btn btn-primary"
                                            onClick = {() => openPdf(String(day.id), `${getDatefromWeekNum(week, index)}.pdf`)}
                                        >
                                            PDF
                                        </button>
                                    ) : null}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {(weekData.emergency ?? []).length ? (
                <div className = "table-responsive mb-4">
                    <table className = "table">
                        <thead className = "noTableTopBorder">
                            <tr>
                                <th>Emergency</th>
                                <th>Date</th>
                                <th>Status</th>
                                <th>Division</th>
                                <th />
                                <th />
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {(weekData.emergency ?? []).map((item: any, index: number) => (
                                <tr key = {index}>
                                    <td>Emergency {index + 1}</td>
                                    <td>{item.date || "N/A"}</td>
                                    <td style = {{ color: statusColorCode(item.status || "Created", "Work") }}>{item.status || "Created"}</td>
                                    <td>{item.division || "N/A"}</td>
                                    <td><Link className = "btn btn-outline-primary" to = {`emergency/${index}/Edit`}>Edit</Link></td>
                                    <td>
                                        <button type = "button" className = "btn btn-danger" onClick = {() => handleDeleteEmergency(index)}>Delete</button>
                                    </td>
                                    <td>
                                        {item.id && String(item.id).length === 32 ? (
                                            <button
                                                type = "button"
                                                className = "btn btn-primary"
                                                onClick = {() => openPdf(String(item.id), `Emergency-${index + 1}.pdf`)}
                                            >
                                                PDF
                                            </button>
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
            <Outlet />
        </div>
    );
}
